import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import ReactTooltip from 'react-tooltip';
import { Switch, Route, Link, useHistory } from 'react-router-dom';

import { useAppData, useIntl } from '../providers';
import {
    CategoryList,
    ProductList,
    CategorySelection,
    ProductSheet,
    AppSection,
    OrderAsAlways,
    ProductOrderForm,
    BranchView,
} from '.';
import { BackButton, Button } from './Buttons';
import { BRANCH_ROUTE, CATEGORY_ROUTE, ORDER_ROUTE, PRODUCT_ROUTE } from '../constants/routes';
import { AppTheme } from '../interfaces';

const useStyles = createUseStyles({
    app: {
        '--primary-color': (props: { theme: AppTheme }) => props.theme.primaryColor || '#91412F',
        '--secondary-color': (props: { theme: AppTheme }) => props.theme.secondaryColor || '#08611F',
        '--deactivated-color': (props: { theme: AppTheme }) => props.theme.deactivatedColor || '#c1c1c1',
    },
    appSection: {
        margin: '3rem 0',
        '& h3': {
            marginBottom: '3rem',
        },
    },
    formWrap: {
        margin: '1rem 0',
        padding: '2rem',
        backgroundColor: '#e6e6e6',
    },
    orderAsAlwaysWrap: {
        marginTop: '2rem',
    },
    tooltip: {
        boxShadow: '0 8px 16px 2px rgba(0, 0, 0, .1)',
    },
    viewAllProductsSection: {
        display: 'flex',
        justifyContent: 'center',
        '& button': {
            width: 'auto',
        },
    },
    limitations: {
        margin: '1rem 0',
    },
});

const ProductFinder: React.FC = () => {
    const { translate } = useIntl();
    const {
        categoryTree,
        selectionLabel,
        selectedCategory,
        parentCategories,
        productsSelection,
        contactsWrapper,
        selectedProduct,
        viewProductsForBranch,
        allowOrderAsAlways,
        isShop,
        inquiriesOnly,
        theme,
    } = useAppData();
    const classes = useStyles({ theme });
    const history = useHistory();
    const [defaultContactsHTML] = useState(contactsWrapper.innerHTML);

    // Returning from the product sheet can lead to the last selected category
    // or the beginning of the app when no category selection happened before
    const handleReturnFromProductSheet = () => {
        if (selectedCategory) {
            history.goBack();
        } else {
            history.push('/');
        }
    };

    const handleReturnFromBranch = () => {
        if (selectedCategory) {
            history.goBack();
        } else {
            history.push('/');
        }
    };

    const handleOrderAsUsual = useCallback(() => {
        history.push(ORDER_ROUTE);
    }, []);

    const handleViewBranch = useCallback(() => {
        viewProductsForBranch(selectedCategory);
    }, [selectedCategory]);

    const handleViewRootBranch = useCallback(() => {
        viewProductsForBranch(parentCategories[0]);
    }, [parentCategories]);

    useEffect(() => {
        ReactTooltip.hide();
        ReactTooltip.rebuild();
    }, [selectedCategory]);

    useEffect(() => {
        contactsWrapper.innerHTML = selectedProduct?.contacts || defaultContactsHTML;
    }, [selectedProduct?.contacts, defaultContactsHTML]);

    return (
        <div className={classes.app}>
            <Switch>
                <Route exact path="/">
                    {productsSelection.length > 0 ? (
                        <AppSection header={translate('selectProduct', 'Our products')}>
                            <ProductList products={productsSelection} />
                        </AppSection>
                    ) : (
                        <AppSection
                            headerType='h1'
                            header={selectionLabel || translate('selectIndustry', 'Please select a subcategory')}
                            >
                            {allowOrderAsAlways && (
                                <div className={classes.orderAsAlwaysWrap}>
                                    <OrderAsAlways enabled={false} onClick={handleOrderAsUsual} />
                                </div>
                            )}

                            <CategoryList categories={selectedCategory?.subCategories || categoryTree} />

                            {isShop && !inquiriesOnly && (
                                <div className={classes.limitations}>
                                    {translate('branchView.limitations', 'We only sell to commercial customers.')}
                                </div>
                            )}
                        </AppSection>
                    )}
                </Route>
                <Route path={ORDER_ROUTE}>
                    <>
                        <BackButton onClick={handleReturnFromProductSheet} />
                        <div className={classes.formWrap}>
                            <ProductOrderForm handleReturn={handleReturnFromProductSheet} />
                        </div>
                    </>
                </Route>
                <Route path={PRODUCT_ROUTE}>
                    <>
                        <BackButton onClick={handleReturnFromProductSheet} />
                        <ProductSheet />
                    </>
                </Route>
                <Route path={CATEGORY_ROUTE}>
                    <>
                        {allowOrderAsAlways && (
                            <div className={classes.orderAsAlwaysWrap}>
                                <OrderAsAlways enabled={false} onClick={handleOrderAsUsual} />
                            </div>
                        )}

                        {parentCategories.length > 0 && (
                            <AppSection>
                                <CategorySelection parentCategories={parentCategories} />
                            </AppSection>
                        )}

                        {selectedCategory?.subCategories.length > 0 && (
                            <AppSection>
                                <div className={classes.viewAllProductsSection}>
                                    <Button
                                        onClick={ isShop ? handleViewBranch : handleViewRootBranch}
                                        label={translate('branchView.link', 'View all products in {branch}', {
                                            branch: isShop ? selectedCategory.label : parentCategories[0].label,
                                        })}
                                    />
                                </div>
                            </AppSection>
                        )}

                        {selectedCategory?.subCategories.length > 0 && (
                            <AppSection
                                header={
                                    selectedCategory.subcategorySelectionLabel ||
                                    translate('selectCategory', 'Please select a subcategory')
                                }
                            >
                                <CategoryList categories={selectedCategory.subCategories} />
                            </AppSection>
                        )}

                        {selectedCategory?.productChoices.length > 0 ? (
                            <>
                                {isShop && (
                                    <div className={classes.viewAllProductsSection}>
                                        <Button
                                            onClick={handleViewRootBranch}
                                            label={translate('branchView.link', 'View all products in {branch}', {
                                                branch: parentCategories[0].label
                                            })}
                                        />
                                    </div>
                                )}
                                <AppSection header={translate('productChoices', 'You have the following choices')}>
                                    <ProductList products={selectedCategory.productChoices} />
                                </AppSection>
                            </>
                        ) : (
                            selectedCategory?.hintIfEmpty && (
                                <>
                                    {isShop && (
                                        <div className={classes.viewAllProductsSection}>
                                            <Button
                                                onClick={handleViewRootBranch}
                                                label={translate('branchView.link', 'View all products in {branch}', {
                                                    branch: parentCategories[0].label
                                                })}
                                            />
                                        </div>
                                    )}
                                    <AppSection>
                                        <div dangerouslySetInnerHTML={{ __html: selectedCategory.hintIfEmpty }} />
                                    </AppSection>
                                </>
                            )
                        )}

                        {selectedCategory?.recommendedProducts.length > 0 && (
                            <AppSection header={translate('productRecommendations', 'Our recommendations for you')}>
                                <ProductList products={selectedCategory.recommendedProducts} />
                            </AppSection>
                        )}

                        {selectedCategory?.description?.length > 0 && (
                            <AppSection>
                                <div dangerouslySetInnerHTML={{ __html: selectedCategory.description }} />
                            </AppSection>
                        )}
                    </>
                </Route>
                <Route path={BRANCH_ROUTE}>
                    <>
                        <BackButton onClick={handleReturnFromBranch} />
                        <BranchView category={selectedCategory} />
                    </>
                </Route>
                <Route path="*">
                    <>
                        <p>{translate('error.pageNotFound', 'No content was found for this url')}</p>
                        <p>
                            <Link to="/">{translate('backToHome', 'Back to the product finder')}</Link>
                        </p>
                    </>
                </Route>
            </Switch>
            <ReactTooltip type="light" className={classes.tooltip} />
        </div>
    );
};

export default ProductFinder;
